import { useState, useCallback, useEffect } from 'react'
import { Competence } from '../types'
import { useTest360Data } from './useTest360Data'

export const useCompetences = (
  _initialCompetences?: Competence[], // Keep for compatibility but will be ignored
  getAnsweredQuestionsCount?: (competenceId: number, totalQuestions: number) => number
) => {
  const { competences: apiCompetences, updateProgress } = useTest360Data()
  const [competences, setCompetences] = useState<Competence[]>(apiCompetences)

  // Update local state when API data changes
  useEffect(() => {
    setCompetences(apiCompetences)
  }, [apiCompetences])

  useEffect(() => {
    if (getAnsweredQuestionsCount && competences.length > 0) {
      setCompetences((prev) => {
        const updated = prev.map((comp) => {
          if (!comp || !comp.id || !comp.questions || !Array.isArray(comp.questions)) {
            return comp
          }

          // Only calculate local progress if the API progress seems incorrect or missing
          // Trust the API values first (answeredQuestions and isCompleted from API)
          const localAnsweredQuestions = getAnsweredQuestionsCount(comp.id, comp.questions.length)
          const localIsCompleted = localAnsweredQuestions === comp.questions.length

          // Use API values if they seem reasonable, otherwise use local calculation
          const finalAnsweredQuestions =
            comp.answeredQuestions >= 0
              ? Math.max(comp.answeredQuestions, localAnsweredQuestions)
              : localAnsweredQuestions

          const finalIsCompleted = comp.isCompleted || localIsCompleted

          // Update progress in Redux store only if there's a change
          if (comp.answeredQuestions !== finalAnsweredQuestions || comp.isCompleted !== finalIsCompleted) {
            updateProgress(comp.id, finalAnsweredQuestions, finalIsCompleted)
          }

          return {
            ...comp,
            answeredQuestions: finalAnsweredQuestions,
            isCompleted: finalIsCompleted,
          }
        })

        return updated
      })
    }
  }, [getAnsweredQuestionsCount, competences.length, updateProgress])

  const updateCompetenceProgress = useCallback((competenceId: number, answeredQuestions: number) => {
    setCompetences((prev) =>
      prev.map((comp) => {
        if (comp.id === competenceId) {
          const isCompleted = answeredQuestions === comp.questions.length
          return {
            ...comp,
            answeredQuestions,
            isCompleted,
          }
        }
        return comp
      })
    )
  }, [])

  const getCompetenceById = useCallback(
    (id: number) => {
      return competences.find((comp) => comp.id === id)
    },
    [competences]
  )

  const completedCount = competences.filter((comp) => comp.isCompleted).length
  const totalCount = competences.length

  return {
    competences,
    updateCompetenceProgress,
    getCompetenceById,
    completedCount,
    totalCount,
  }
}
